/**
 * DashboardStatTile Component
 * Displays a single statistic card with icon, number, and label
 *
 * NÃO é o `StatCard` de `components/common/DetailView` — este é o tile grande do
 * dashboard (número em `h3`, `trend`/`changePercent`, card clicável), tipado por
 * `types/dashboard`. Só o nome coincidia; a API e o visual são outros.
 *
 * @version 2025.1.0
 */
import React from 'react';
import { Card, CardContent, Typography, Box, alpha, useTheme } from '@mui/material';
import { TrendingUp, TrendingDown, TrendingFlat } from '@mui/icons-material';
import { StatCardProps } from '../../../types/dashboard';

const DashboardStatTile: React.FC<StatCardProps> = ({
  icon,
  total,
  label,
  color = 'primary',
  trend,
  changePercent,
  onClick,
}) => {
  const theme = useTheme();

  const getTrendIcon = () => {
    if (!trend) return null;
    switch (trend) {
      case 'up':
        return <TrendingUp sx={{ fontSize: 16, color: theme.palette.success.main }} />;
      case 'down':
        return <TrendingDown sx={{ fontSize: 16, color: theme.palette.error.main }} />;
      case 'stable':
        return <TrendingFlat sx={{ fontSize: 16, color: theme.palette.grey[500] }} />;
      default:
        return null;
    }
  };

  const getTrendColor = () => {
    if (!trend) return 'text.secondary';
    switch (trend) {
      case 'up':
        return 'success.main';
      case 'down':
        return 'error.main';
      default:
        return 'text.secondary';
    }
  };

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.2s ease-in-out',
        '&:hover': onClick
          ? {
              transform: 'translateY(-2px)',
              boxShadow: 4,
            }
          : {},
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h3"
              component="div"
              sx={{
                fontWeight: 700,
                color: `${color}.main`,
                mb: 0.5,
              }}
            >
              {total.toLocaleString('pt-BR')}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 500 }}
            >
              {label}
            </Typography>
            {trend && changePercent !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                {getTrendIcon()}
                <Typography variant="caption" color={getTrendColor()}>
                  {changePercent > 0 ? '+' : ''}
                  {changePercent}% vs. mês anterior
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: alpha(theme.palette[color].main, 0.1),
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default DashboardStatTile;
